import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Camera } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import DirectAnswer from '../components/DirectAnswer';
import { Button } from '../components/ui/button';
import { CaseCard } from '../components/gallery/CaseGrid';
import { useGallery, imgUrl } from '../components/gallery/BeforeAfterSlider';
import { PUBLIC_SITE_URL } from '../data/seo';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { getBreadcrumbLD, getImageGalleryLD, getFAQPageLD } from '../data/structuredData';

const galleryFAQs = [
  { question: 'Are these real Restore.Dental patients?', answer: 'Yes. Every before-and-after photo in this gallery is an actual patient treated by Dr Kartik Patel at 64a Horn Lane, Acton, published with written consent. Images are not retouched beyond cropping and are watermarked to protect our patients.' },
  { question: 'Will my results look like these?', answer: 'Every mouth is different, so results vary. During your consultation we use Digital Smile Design and 3D scanning to show you a personalised preview of what is achievable for your teeth before you commit.' },
  { question: 'How can I see more cases?', answer: 'We hold a much larger portfolio in practice. Book a free consultation and we will show you cases that match your starting point and goals.' },
];

export default function BeforeAfter() {
  const [filter, setFilter] = useState('');
  const { cases, treatments, loading } = useGallery(filter || undefined);
  const crumbs = [{ name: 'Home', to: '/' }, { name: 'Before & After Gallery', to: '/before-after' }];
  const seo = {
    title: 'Before & After Photos — Real Patient Results | Restore Dental Acton',
    description: 'Real before-and-after photos from Restore.Dental in Acton: Invisalign, composite bonding, implants, veneers and smile makeovers by Dr Kartik Patel BDS.',
    path: '/before-after',
    keywords: ['before and after teeth', 'composite bonding before and after', 'invisalign before and after', 'dental implants before after', 'smile makeover london'],
    image: cases[0] ? imgUrl(cases[0].after_url) : undefined,
  };
  return (
    <div className="min-h-screen bg-white">
      <SEO
        {...seo}
        structuredData={[
          getImageGalleryLD(cases),
          getBreadcrumbLD(crumbs.map((c) => ({ name: c.name, url: `${PUBLIC_SITE_URL}${c.to}` }))),
          getFAQPageLD(galleryFAQs),
        ]}
      />
      <PageHeader />
      <main className="pt-28 md:pt-32">
        <section className="container mx-auto px-4 max-w-6xl">
          <Breadcrumbs items={crumbs} />
          <p className="text-sm font-semibold uppercase tracking-widest text-[#006A71] mt-6 mb-3">Smile gallery · real patients</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-rabbit leading-tight mb-6">Before &amp; After: Real Results from Acton</h1>
          <DirectAnswer
            answer="These are genuine before-and-after photographs of Restore.Dental patients treated by Dr Kartik Patel BDS in Acton, West London — Invisalign, composite bonding, dental implants, veneers and full smile makeovers. Drag the slider on any case to compare. Every image is published with the patient's written consent and watermarked; results vary and your own preview is created at consultation."
            bullets={['Drag any slider to compare', 'Published with written consent', 'Unretouched, watermarked images', 'Filter by treatment below']}
          />

          <div className="flex flex-wrap gap-2 mt-8" role="tablist" aria-label="Filter by treatment">
            <button type="button" onClick={() => setFilter('')} className={`px-4 py-2 rounded-full border text-sm ${!filter ? 'bg-rabbit text-white border-rabbit' : 'border-sky-gray/30 text-rabbit hover:bg-sky-gray/10'}`} data-testid="gallery-filter-all">All treatments</button>
            {treatments.map((t) => (
              <button key={t} type="button" onClick={() => setFilter(t)} className={`px-4 py-2 rounded-full border text-sm ${filter === t ? 'bg-rabbit text-white border-rabbit' : 'border-sky-gray/30 text-rabbit hover:bg-sky-gray/10'}`} data-testid={`gallery-filter-${t.toLowerCase().replace(/\s+/g, '-')}`}>{t}</button>
            ))}
          </div>
        </section>

        <section className="container mx-auto px-4 max-w-6xl mt-10">
          {loading && <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-sky-gray/10 animate-pulse" />)}</div>}
          {!loading && cases.length === 0 && (
            <div className="text-center py-16 border-2 border-dashed border-sky-gray/30 rounded-2xl" data-testid="gallery-empty">
              <Camera className="w-12 h-12 text-burnt-coral mx-auto mb-4" aria-hidden="true" />
              <p className="text-lg font-semibold text-rabbit mb-2">New cases are being added</p>
              <p className="text-chocolate-malt">Our full portfolio is available to view in practice. Book a free consultation to see cases like yours.</p>
            </div>
          )}
          {!loading && cases.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
              {cases.map((item) => <CaseCard key={item.id} item={item} />)}
            </div>
          )}
        </section>

        <section className="bg-sky-gray/10 py-16 mt-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">About our gallery</h2>
            <div className="space-y-6">
              {galleryFAQs.map((f) => (
                <div key={f.question} className="border-b border-sky-gray/20 pb-6">
                  <h3 className="text-lg font-bold text-rabbit mb-2">{f.question}</h3>
                  <p className="text-chocolate-malt leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"><a href={PRACTICE.phoneTel} data-testid="gallery-call-btn"><Phone className="w-5 h-5 mr-2" />Book a free consultation</a></Button>
              <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BA5A] text-white"><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="gallery-whatsapp-btn">Send us a photo of your smile</a></Button>
              <Button asChild size="lg" variant="outline"><Link to="/pricing">See treatment prices</Link></Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
